import toast from 'react-hot-toast'

import { useMutation, useQueryClient } from '@fv/client-core'
import type { UpdateUserPreferenceDTO } from '@fv/models'

import { supportMessage } from '@/constants'
import {
  type UserSettings,
  userSettingsKey,
} from '@/hooks/settings/useUserSettings'
import type { ApiError } from '@/types/ApiError'
import { shipperFetch } from '@/utils/shipperFetch'

import { accountUserKeys } from '../../account-users/queries'
import type { UserPreferenceKey } from './types'

type PartialAccountUser = Pick<UserSettings, 'preferences'>
type UpdateType = UpdateUserPreferenceDTO['type']
type UpdateValue<TType extends UpdateType> = Extract<
  UpdateUserPreferenceDTO,
  { type: TType }
>['value']

async function updateUserPreference(
  body: UpdateUserPreferenceDTO,
): Promise<PartialAccountUser> {
  return shipperFetch<PartialAccountUser>('/accounts/user-preference', {
    body,
    method: 'POST',
  })
}

function setCachedPreferences(
  queryClient: ReturnType<typeof useQueryClient>,
  preferences: UserSettings['preferences'],
) {
  queryClient.setQueriesData<UserSettings | undefined>(
    { queryKey: userSettingsKey },
    prev =>
      prev && {
        ...prev,
        preferences,
      },
  )
}

type UseUpdateUserPreferenceOptions<TType extends UpdateType> = {
  type: TType
  successMessage?: string
  afterSuccess?: () => void
}

function useUpdateUserPreference<TType extends UpdateType>(
  options: UseUpdateUserPreferenceOptions<TType>,
) {
  const queryClient = useQueryClient()
  const { afterSuccess, successMessage, type } = options

  const mutationFn = (value: UpdateValue<TType>) =>
    updateUserPreference({ type, value } as Extract<
      UpdateUserPreferenceDTO,
      { type: TType }
    >)

  return useMutation(mutationFn, {
    onSuccess: ({ preferences }: PartialAccountUser) => {
      setCachedPreferences(queryClient, preferences)

      afterSuccess?.()
      if (successMessage) {
        toast.success(successMessage)
      }
    },
  })
}

export function useUpdateFavoriteEquipment() {
  return useUpdateUserPreference({
    successMessage: 'Favorite equipment updated successfully.',
    type: 'equipment-types',
  })
}

export function useUpdateSubscribedVendors() {
  const queryClient = useQueryClient()

  return useUpdateUserPreference({
    afterSuccess: () => {
      queryClient.invalidateQueries({ queryKey: accountUserKeys.all })
    },
    successMessage: 'Vendor notification preferences updated successfully.',
    type: 'subscribed-vendors',
  })
}

export function useUpdateColumnPreferences() {
  return useUpdateUserPreference({
    successMessage: 'Column preferences updated successfully.',
    type: 'condensed-columns',
  })
}

export function useUpdateShipmentListPreference() {
  return useUpdateUserPreference({
    type: 'shipment-list',
  })
}

export function useUpdateLandingPagePreference() {
  return useUpdateUserPreference({
    type: 'landing-page',
  })
}

async function toggleUserPreference(dto: {
  feature: UserPreferenceKey
  value: boolean
}): Promise<PartialAccountUser> {
  return shipperFetch<PartialAccountUser>('/accounts/toggle-user-preference', {
    body: dto,
    method: 'POST',
  })
}

export function useToggleUserPreference(
  feature: UserPreferenceKey,
  notify?: boolean, // toast message on response
) {
  const queryClient = useQueryClient()
  const mutation = useMutation(toggleUserPreference, {
    onMutate: ({ value }) => {
      // Update cache immediately to drive UI
      queryClient.setQueriesData<UserSettings | undefined>(
        { queryKey: userSettingsKey },
        prev =>
          prev && {
            ...prev,
            preferences: {
              ...prev.preferences,
              [feature]: value,
            },
          },
      )
    },
    onSuccess: () => {
      notify &&
        toast.success('Preference updated successfully.', {
          id: 'user-preference-update',
        })
    },
  })

  function setPreference(value: boolean) {
    mutation.mutate(
      { feature, value },
      {
        onError: e => {
          const error = e as ApiError

          // Roll back cache update
          queryClient.setQueriesData<UserSettings | undefined>(
            { queryKey: userSettingsKey },
            prev =>
              prev && {
                ...prev,
                preferences: {
                  ...prev.preferences,
                  [feature]: !value,
                },
              },
          )

          if (notify) {
            let message = 'Unable to update preference'
            if (error?.message) message += `: ${error.message}`
            else message += `, ${supportMessage}`
            toast.error(message)
          }
        },
      },
    )
  }

  return {
    isBusy: mutation.isPending,
    setPreference,
  }
}
